import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { toggleAdditionalSidebar, setSidebarDefaultState } from '../redux/ui';
import type { RootState } from '../app/store/store';

interface UseSidebarOptions {
  sidebarKey: string;
  defaultOpen?: boolean;
  autoInitialize?: boolean;
}

interface UseSidebarReturn {
  isOpen: boolean;
  toggle: (isOpen?: boolean) => void;
  open: () => void;
  close: () => void;
}

export const useSidebar = ({
  sidebarKey,
  defaultOpen = false,
  autoInitialize = true
}: UseSidebarOptions): UseSidebarReturn => {
  const dispatch = useAppDispatch();
  const additionalSidebars = useAppSelector((state: RootState) => state.ui.additionalSidebars);

  // Use defaultOpen until Redux has an explicit value for this sidebar
  const isOpen = additionalSidebars.hasOwnProperty(sidebarKey) ? additionalSidebars[sidebarKey] : defaultOpen;

  // Initialize the sidebar state if autoInitialize is true and the sidebar doesn't exist.
  // Restores the previous open/closed state from localStorage when available.
  useEffect(() => {
    if (autoInitialize && !additionalSidebars.hasOwnProperty(sidebarKey)) {
      const stored = localStorage.getItem(`sidebar:${sidebarKey}`);
      const initialOpen = stored !== null ? stored === 'true' : defaultOpen;
      dispatch(setSidebarDefaultState({ sidebarKey, defaultOpen: initialOpen }));
    }
  }, [dispatch, sidebarKey, defaultOpen, autoInitialize, additionalSidebars]);

  // Persist sidebar state to localStorage whenever it changes
  useEffect(() => {
    if (additionalSidebars.hasOwnProperty(sidebarKey)) {
      localStorage.setItem(`sidebar:${sidebarKey}`, String(isOpen));
    }
  }, [sidebarKey, isOpen, additionalSidebars]);

  const toggle = useCallback(
    (isOpen?: boolean) => {
      dispatch(toggleAdditionalSidebar({ sidebarKey, isOpen }));
    },
    [dispatch, sidebarKey]
  );

  const open = useCallback(() => {
    dispatch(toggleAdditionalSidebar({ sidebarKey, isOpen: true }));
  }, [dispatch, sidebarKey]);

  const close = useCallback(() => {
    dispatch(toggleAdditionalSidebar({ sidebarKey, isOpen: false }));
  }, [dispatch, sidebarKey]);

  return {
    isOpen,
    toggle,
    open,
    close
  };
};
